<template>
  <div
    class="border p-4 rounded-sm group"
    :class="{ 'border-gray-500': !newItem, 'border-black': newItem }"
  >
    <div
      class="
        grid grid-cols-1-auto
        text-gray-300
        group-hover:text-gray-900
        transition-colors
        duration-150
      "
      v-if="!newItem"
    >
      <div class="drag-handle cursor-move pb-2">
        <icon name="DragMove" size="1.5em" />
      </div>
      <div>
        <wd-button transparent @input="sendDelete">
          <icon name="Trash" size="1.5em" />
        </wd-button>
      </div>
    </div>
    <div
      class="-mt-4 -mx-4 bg-black text-white mb-2 p-1 px-2 tracking-wide"
      v-if="newItem"
    >
      <icon
        class="-mt-1 mr-1"
        size="1.2em"
        :settings="{ strokeWidth: 1.6 }"
        name="Info"
      />
      {{ $t("news.manage.unsaved_item") }}
    </div>
    <form @submit.prevent="submit(formOptions)">

    <div>
      <input-select
        class="max-w-md"
        :label="$t('news.entry_item_type')"
        :options="entryTypes"
        optionlabel="title"
        optionvalue="id"
        :value="formData.type"
        required
        @update-value="updateForm('type', $event, !!item.id ? item.id : false)"
      />
    </div>
    <div
      :class="{ 'grid grid-cols-1 md:grid-cols-2 gap-2': preview }"
      v-if="itemType === 'text'"
    >
      <div class="my-2">
        <input-textarea
          :value="formData.value"
          :label="$t('news.value')"
          required
          @update-value="
            updateForm('value', $event, !!item.id ? item.id : false)
          "
        />
      </div>
      <div class="mt-6" v-if="preview">
        <markdown-preview-box :text="formData.value" />
      </div>
    </div>
    <div class="stack mt-2" v-if="itemType === 'youtube'">
      <input-text
        :value="formData.value"
        label="Youtube-Link"
        required
        @update-value="updateForm('value', $event, !!item.id ? item.id : false)"
      />
      <div v-if="formData.value && realLink(formData.value)">
        <youtube :url="formData.value" />
      </div>
    </div>
    <div class="stack mt-2 cursor-pointer" v-if="itemType === 'image'">
      <div @click="toggleImageSelect">
        <label v-if="formData.image_id">{{ $t("news.news_image") }}</label>
        <div class="border border-primary-500 p-2" v-if="formData.image_id">
          <single-image :image-id="formData.image_id" />
        </div>
        <div class="border border-primary-500 p-2" v-else>
          <icon class="cursor-pointer" name="Image" size="1.5em" />
          <span class="ml-2">{{ $t("news.news_image") }}</span>
        </div>
      </div>
    </div>
    <div class="stack mt-2" v-if="itemType === 'survey'">
      <!-- Survey picker -->
      <div v-if="formData.survey_id">
        <survey
          :survey="getSurvey(formData.survey_id)"
          select
          edit
          @selection="removeSurvey"
        />
      </div>
      <div v-else>
        <input-text
          label="Frage des Fragebogens"
          @update-value="searchValue = $event"
        />

        <div v-if="filteredSurveys.length > 0">
          <survey
            v-for="survey in filteredSurveys"
            :key="survey.id"
            :survey="survey"
            select
            @selection="updateForm('survey_id', survey.id)"
          />
        </div>
      </div>
    </div>
    <wd-button :sending="sending" propagate class="mt-4">{{
      newItem ? $t("general.create") : $t("general.update")
    }}</wd-button>
    </form>
    <images
      v-if="imageSelect"
      @close="toggleImageSelect"
      @selection="updateImage"
    />
  </div>
</template>

<script>
import FormMixin from "@/mixins/FormMixin";

import { NewsEntryItem } from "@/modules/news/viewmodels/NewsEntryItem";
import { NewsEntry } from "@/modules/news/viewmodels/NewsEntry";

const MarkdownPreviewBox =
  require("@/modules/news/components/MarkdownPreviewBox.vue").default;

const Youtube = require("@/modules/news/components/Youtube.vue").default;
const InputSelect =
  require("@/modules/input/components/InputSelect.vue").default;
const InputTextarea =
  require("@/modules/input/components/InputTextarea.vue").default;
const Images = require("@/modules/images/components/Images.vue").default;
const SingleImage =
  require("@/modules/images/components/SingleImage.vue").default;
const Survey = require("@/modules/surveys/components/Survey.vue").default;

export default {
  name: "NewsEntryItemForm",
  mixins: [FormMixin],
  data() {
    return {
      entryTypes: [
        {
          title: "Text",
          id: "text",
        },
        {
          title: "Youtube",
          id: "youtube",
        },
        {
          title: "Bild",
          id: "image",
        },
        {
          title: "Frage",
          id: "survey",
        },
      ],
      newItem: false,
      deleteSending: false,
      searchValue: null,
      imageSelect: false,
    };
  },
  props: {
    item: {
      type: NewsEntryItem,
    },
    newsEntryId: {
      type: String,
    },
    preview: {
      default: false,
      type: Boolean,
    },
  },
  components: {
    InputSelect,
    InputTextarea,
    Youtube,
    MarkdownPreviewBox,
    Survey,
    Images,
    SingleImage,
  },
  methods: {
    realLink(Url) {
      return Url.includes("watch?v=");
    },
    sendDelete() {
      if (this.newItem) {
        this.emitClose();
      } else {
        this.$store.dispatch("dialog/add", {
          title: "Löschen bestätigen",
          text: `Inhalt wirklich löschen?`,
          submitFn: () => {
            this.submit(this.deleteFormOptions);
          },
        });
      }
    },
    emitClose() {
      this.$emit("close");
    },
    getSurvey(Id) {
      return this.surveys.find((e) => e.id === Id);
    },
    removeSurvey() {
      this.formData.survey_id = null;
    },
    toggleImageSelect() {
      this.imageSelect = !this.imageSelect;
    },
    updateImage(Image) {
      this.toggleImageSelect();
      this.updateForm("image_id", Image);
    },
  },
  computed: {
    itemType() {
      return this.formData.type;
    },
    surveys() {
      return this.$store.getters["survey/surveys"];
    },
    filteredSurveys() {
      let filteredSurveys = this.surveys;
      if (this.searchValue) {
        filteredSurveys = this.surveys.filter((e) =>
          e.question.toUpperCase().includes(this.searchValue.toUpperCase())
        );
      }

      return filteredSurveys.slice(0, 5);
    },
  },
  async created() {
    await this.$store.getters["survey/api"].GetAll();
    if (!this.item.id) {
      this.newItem = true;
      this.formData.news_entry_id = this.newsEntryId;

      this.formOptions = {
        async: true,
        type: "newsentryitem",
        call: "create",
        action: (response) => {
          this.emitClose();
        },
      };
    } else {
      this.formData = this.item;

      this.formOptions = {
        async: true,
        type: "newsentryitem",
        call: "update",
        id: this.item.id,
        action: (response) => {
          this.$store.dispatch("temp/removeUnsavedItem", {item: this.item.id});
        },
      };
    }

    this.deleteFormOptions = {
      async: true,
      type: "newsentryitem",
      call: "destroy",
      sendingVar: false,
      id: this.item.id,
      action: (response) => {
        this.deleteSending = false;
      },
    };
  },
};
</script>
